import React, { Suspense } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { AppShell, AnimatedPage, type NavSection } from '@theonefamily/ui';

const HubBar = React.lazy(() => import('hubBar/HubBar'));
import { useAuth } from '@/hooks/useAuth';
import {
  LayoutDashboard,
  Sparkles,
  Bot,
  Shield,
  Store,
  Cpu,
  BarChart3,
  Settings,
  Brain,
  MessageSquare,
} from 'lucide-react';

const NAVIGATION: NavSection[] = [
  {
    items: [
      { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, href: '/dashboard' },
      { id: 'ai-chat', label: 'The One AI', icon: Sparkles, href: '/ai' },
      { id: 'ai-memory', label: 'Memory', icon: Brain, href: '/ai/memory' },
      { id: 'playground', label: 'Playground', icon: MessageSquare, href: '/playground' },
      { id: 'agents', label: 'AI Agents', icon: Bot, href: '/agents' },
      { id: 'governance', label: 'Governance', icon: Shield, href: '/governance' },
      { id: 'marketplace', label: 'Marketplace', icon: Store, href: '/marketplace' },
      { id: 'models', label: 'Models', icon: Cpu, href: '/governance/models' },
      { id: 'usage', label: 'Usage', icon: BarChart3, href: '/usage' },
      { id: 'admin', label: 'Admin', icon: Settings, href: '/settings' },
    ],
  },
];

export function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const currentTenant = user?.tenants.find((t) => t.id === user.current_tenant_id);

  const handleSignOut = async () => {
    await logout();
    // logout() now redirects to Hub — no need to navigate locally
  };

  return (
    <>
      <Suspense fallback={<div style={{ height: 48 }} />}>
        <HubBar
          currentProduct="ai-studio"
          apiBase={import.meta.env.VITE_BUS_URL ?? 'https://api.theonebus.app'}
          hubUrl={import.meta.env.VITE_HUB_URL}
          supportConfig={{
            apiBaseUrl: import.meta.env.VITE_OPS_CENTER_API_URL ?? 'https://api.theoneops.app',
            apiKey: import.meta.env.VITE_OPS_CENTER_API_KEY ?? '',
            platformId: 'ai-studio',
            platformName: 'The One AI Studio',
          }}
        />
      </Suspense>
      <AppShell
        product={{ name: 'The One AI Studio', accent: '#6366f1' }}
        navigation={NAVIGATION}
        user={{
          name: user?.user.name ?? user?.user.email ?? 'User',
          email: user?.user.email ?? '',
          role: (user?.user as Record<string, unknown>)?.role as string | undefined,
        }}
        tenant={currentTenant ? { name: currentTenant.name, slug: currentTenant.id } : undefined}
        tenants={user?.tenants.map((t) => ({ name: t.name, slug: t.id }))}
        onSignOut={() => { void handleSignOut(); }}
        onProfileClick={() => void navigate('/settings')}
      >
        <AnimatedPage>
          <Outlet />
        </AnimatedPage>
      </AppShell>
    </>
  );
}
